import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { loggerFunc } from 'utils/logger';
import useUserProducts from 'utils/useUserProducts';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import * as RouteConstants from 'constants/routeConstants';
import VehicleDetailsMain from 'components/VehicleRemindersPage/Reminders/VehicleRemindersPage';
// LoggedOut routes
import FAQsFrequentQuestions from 'views/FAQ/FrequentQuestions';
import FAQLoggingIn from 'views/FAQ/LoggingIn';
import FAQPolicyDetails from 'views/FAQ/MyMembership';
import FAQPersonalDetails from 'views/FAQ/PersonalDetails';
import RegistrationLogin from 'views/RegistrationLogin/RegistrationLogin';
import Logout from 'views/RegistrationLogin/Logout';
import SessionExpired from 'views/RegistrationLogin/SessionExpired';
import PageNotFound from 'views/MyRac/PageNotFound';
// LoggedIn routes
import { useApplicationState } from 'hooks/applicationState-Context';
import { populateMessages } from 'services/MessageService';
import LoggedInView from 'layout/loggedInView/LoggedInView';
import RequireAuth from 'RequireAuth';
import MainContent from 'layout/mainContent/MainContent';
import AccountSettings from 'views/MyRac/AccountSettings/AccountSettings';
import MyCover from 'views/MyRac/MyCover/MyCover';
// RAC Loyalty Rewards
import LoyaltyRewardsView from 'layout/loyaltyRewardsView/LoyaltyRewardsView';
import LoyaltyRewardsOptions from 'views/MyRac/Journeys/LoyaltyRewards/Options';
import LoyaltyRewardsConfirmation from 'views/MyRac/Journeys/LoyaltyRewards/Confirmation';
// Personal detail pages
import CarInsuranceScreen from 'views/MyRac/MyCover/CarInsurance/Screens/CarInsuranceScreen';
import MembershipOverview from 'views/MyRac/MembershipOverview/MembershipOverview';
import MembershipDocuments from 'views/MyRac/MembershipDocuments';
import MemberSavings from 'views/MyRac/MemberSavings';
import PersonalDetails from 'views/MyRac/PersonalDetails';
import UpdatePassword from 'views/MyRac/UpdatePassword';
import TermsAndConditions from 'views/MyRac/TermsAndConditionsPage';
import VulnerableCustomers from 'views/MyRac/VulnerableCustomers/VulnerableCustomers';
import MarketingPermission from 'views/MyRac/MarketingPermission/MarketingPermission';
// Product Upgrade
import { useUserJourney } from 'hooks/userJourney-Context';
import { TACTICAL_UPGRADE_JOURNEY } from 'constants/journeyManagerStepConstants';
import JourneyManager from 'views/MyRac/ProductUpgrade/JourneyManager/JourneyManager';
import ProductUpgradeJourney from 'views/MyRac/ProductUpgrade/ProductUpgradeJourney';
import PaymentPage from 'views/MyRac/ProductUpgrade/PaymentPage/PaymentPage';
import UpgradeConfirmationPage from 'views/MyRac/ProductUpgrade/UpgradeConfirmationPage';
import PaymentFailedPage from 'views/MyRac/ProductUpgrade/PaymentFailedPage';
import PaymentIssue from 'views/MyRac/PaymentIssue';
// Renewal
import RenewalsView from 'layout/renewalsView/RenewalsView';
import Renewal from 'views/MyRac/ExpiryRenewal/Renewal';
import RenewalCustomise from 'views/MyRac/ExpiryRenewal/Customise';
import RenewalSummary from 'views/MyRac/ExpiryRenewal/Summary';
import RenewalCardPayment from 'views/MyRac/ExpiryRenewal/CardPayment';
import RenewalDirectDebit from 'views/MyRac/ExpiryRenewal/DirectDebit';
import RenewalCombinedPayments from 'views/MyRac/ExpiryRenewal/CombinedPayments';
import RenewalPaymentOptions from 'views/MyRac/ExpiryRenewal/PaymentOptions';
import RenewalConfirmation from 'views/MyRac/ExpiryRenewal/Confirmation';
import RenewalError from 'views/MyRac/ExpiryRenewal/Error';
// B2B Breakdown Cover
import B2B2CBreakdownCoverScreen from 'views/MyRac/MyCover/B2B2CBreakdown/B2B2CBreakdownScreen';

// Logged in routes styles
import './myRac.scss';

const MyRac = () => {
  const { userJourneyState, storePreviousUrlPath, storeCurrentUrlPath } = useUserJourney();
  const { setPageLoading } = useApplicationState();
  const featureFlags = useSelector((state) => state.featureToggle)

  const location = useLocation();
  const isCarInsuranceRoute = location.pathname === RouteConstants.CAR_INSURANCE;
  const logger = loggerFunc('MyRAC');

  useEffect(() => {
    async function reloadMessages() {
      try {
        await populateMessages();
        logger.debug('Messages loaded.');
      } catch (e) {
        logger.error(e);
      }
    }
    reloadMessages();
  }, []);

  useEffect(() => {
    storeCurrentUrlPath(location.pathname);
    storePreviousUrlPath(location.pathname);
  }, [location]);

  useEffect(() => {
    // If location is in API ROUTE LIST initiate page loading spinner which will be set to false once that page's API request has complete. Also checks to see if the page the customer is trying to navigate to is the same as the current page.
    if (RouteConstants.API_ROUTE_LIST.includes(location.pathname) && location.pathname !== userJourneyState.currentUrl) {
      setPageLoading(true);
    } else {
      setPageLoading(false);
    }
  }, [location]);

  const { isIMAccount, hasB2BBreakdownPolicy, isCarInsuranceOnly, hasCarInsurancePolicy } = useUserProducts();

  const coreRoutes = useMemo(() => ([
    // IM Routes
    ...(isIMAccount ? [
      { path: RouteConstants.ROOT, element: <RequireAuth><MembershipOverview /></RequireAuth> },
      { path: RouteConstants.MY_COVER, element: <RequireAuth><MyCover /></RequireAuth> },
      { path: RouteConstants.BREAKDOWN_PRODUCTS, element: <Navigate replace to={RouteConstants.MY_COVER} /> },
      { path: RouteConstants.VEHICLE_DETAILS, element: <RequireAuth><VehicleDetailsMain /></RequireAuth> },
      { path: RouteConstants.PRODUCT_UPGRADE_JOURNEY, element: <RequireAuth><JourneyManager journeyName={TACTICAL_UPGRADE_JOURNEY} journeySteps={[ProductUpgradeJourney]} /></RequireAuth> },
      { path: RouteConstants.PRODUCT_UPGRADE_PAGE, element: <RequireAuth><JourneyManager journeyName={TACTICAL_UPGRADE_JOURNEY} journeySteps={[ProductUpgradeJourney, PaymentPage]} /></RequireAuth> },
      { path: RouteConstants.PAYMENT_PAGE, element: <RequireAuth><PaymentPage /></RequireAuth> },
      { path: RouteConstants.PAYMENT_ISSUE, element: <RequireAuth><PaymentIssue /></RequireAuth> },
      { path: RouteConstants.PRODUCT_UPGRADE_CONFIRMATION, element: <RequireAuth><UpgradeConfirmationPage /></RequireAuth> },
      { path: RouteConstants.PRODUCT_UPGRADE_PAYMENT_CONFIRMATION, element: <RequireAuth><UpgradeConfirmationPage isPaymentJourney /></RequireAuth> },
      { path: RouteConstants.PRODUCT_UPGRADE_FAILED, element: <RequireAuth><PaymentFailedPage /></RequireAuth> },
      { path: RouteConstants.DOCUMENTS, element: <RequireAuth><MembershipDocuments /></RequireAuth> },
      { path: RouteConstants.MEMBERS_RECOMMEND_A_FRIEND, element: <RequireAuth><MembershipOverview /></RequireAuth> },
      { path: RouteConstants.FAQS_MY_POLICY_DETAILS, element: <FAQPolicyDetails /> },
      { path: RouteConstants.FAQS_MY_PERSONAL_DETAILS, element: <FAQPersonalDetails /> },
      { path: RouteConstants.TESCOLOGIN, element: <Navigate replace to={RouteConstants.ROOT} /> },
      { path: RouteConstants.TESCOLOGINBREAKDOWNCOVER, element: <Navigate replace to={RouteConstants.ROOT} /> },
      { path: RouteConstants.TESCOLOGINRENEWAL, element: <Navigate replace to={RouteConstants.ROOT} /> },
      { path: RouteConstants.PERSONAL_DETAILS, element: <RequireAuth><PersonalDetails /></RequireAuth> },
      { path: RouteConstants.VULNERABLE_CUSTOMERS, element: <RequireAuth><VulnerableCustomers /></RequireAuth> },
      { path: RouteConstants.MARKETING_PERMISSION, element: <RequireAuth><MarketingPermission /></RequireAuth> },
    ] : []),

    // Renewal route redirect
    ...(isIMAccount && featureFlags?.isLDReady && featureFlags?.allowRenewalJourney !== 1 ? [
      { path: RouteConstants.RENEWAL, element: <Navigate replace to={RouteConstants.ROOT} /> },
    ] : []),

    // Car insurance route
    ...(featureFlags?.isLDReady && featureFlags?.enableCarInsuranceWeb && hasCarInsurancePolicy ? [
      { path: RouteConstants.CAR_INSURANCE, element: <RequireAuth><CarInsuranceScreen /></RequireAuth> },
    ] : []),

    // Reroute when trying to access / for car insurance/b2b
    ...(hasB2BBreakdownPolicy || isCarInsuranceOnly ? [
      { path: RouteConstants.ROOT, element: <Navigate replace to={RouteConstants.CAR_INSURANCE} /> },
    ] : []),

    // /my-cover route for b2b
    ...(hasB2BBreakdownPolicy ? [
      { path: RouteConstants.MY_COVER, element: <RequireAuth><B2B2CBreakdownCoverScreen /></RequireAuth> },
    ] : []),

    // Routes all users can access
    { path: RouteConstants.MEMBER_SAVINGS, element: <RequireAuth><MemberSavings /></RequireAuth> },
    { path: RouteConstants.ACCOUNT_DETAILS, element: <RequireAuth><AccountSettings /></RequireAuth> },
    { path: RouteConstants.UPDATE_PASSWORD, element: <RequireAuth><UpdatePassword /></RequireAuth> },
    { path: RouteConstants.FAQS, element: <FAQsFrequentQuestions /> },
    { path: RouteConstants.FAQS_LOGGING_IN_TO_MYRAC, element: <FAQLoggingIn /> },
    { path: RouteConstants.TERMS_CONDITIONS, element: <TermsAndConditions /> },
    { path: RouteConstants.FORGOT_PASSWORD, element: <UpdatePassword /> },
    { path: RouteConstants.LOGOUT, element: <Logout /> },
    { path: RouteConstants.SESSION_EXPIRED, element: <SessionExpired /> },
    { path: '*', element: <PageNotFound /> },
  ]), [isIMAccount, hasB2BBreakdownPolicy, isCarInsuranceOnly, featureFlags?.isLDReady, featureFlags?.allowRenewalJourney, featureFlags?.enableCarInsuranceWeb, hasCarInsurancePolicy])

  const renewalRoutes = useMemo(() => ([
    ...(isIMAccount ? [
      { index: true, element: <RequireAuth><Renewal /></RequireAuth> },
      { path: RouteConstants.RENEWAL_CUSTOMISE, element: <RequireAuth><RenewalCustomise /></RequireAuth> },
      { path: RouteConstants.RENEWAL_SUMMARY, element: <RequireAuth><RenewalSummary /></RequireAuth> },
      { path: RouteConstants.RENEWAL_PAYMENT, element: <RequireAuth><RenewalCardPayment /></RequireAuth> },
      { path: RouteConstants.RENEWAL_DIRECTDEBIT, element: <RequireAuth><RenewalDirectDebit /></RequireAuth> },
      { path: RouteConstants.RENEWAL_COMBINED_PAYMENTS, element: <RequireAuth><RenewalCombinedPayments /></RequireAuth> },
      { path: RouteConstants.RENEWAL_PAYMENT_OPTIONS, element: <RequireAuth><RenewalPaymentOptions /></RequireAuth> },
      { path: RouteConstants.RENEWAL_PREVIOUS_PAYMENT, element: <RequireAuth><RenewalCardPayment usePreviousCardDetails /></RequireAuth> },
      { path: RouteConstants.RENEWAL_CONFIRMATION, element: <RequireAuth><RenewalConfirmation /></RequireAuth> },
      { path: RouteConstants.RENEWAL_ERROR, element: <RequireAuth><RenewalError /></RequireAuth> },
    ] : [
      { index: true, element: <PageNotFound /> },
    ]),
  ]), [isIMAccount])

  const royaltyRewardsRoutes = useMemo(() => ([
    ...(isIMAccount ? [
      { index: true, element: <RequireAuth><LoyaltyRewardsOptions /></RequireAuth> },
      { path: RouteConstants.RAC_LOYALTY_REWARDS_CONFIRMATION, element: <RequireAuth><LoyaltyRewardsConfirmation /></RequireAuth> },
    ] : [
      { index: true, element: <PageNotFound /> },
    ]),
  ]), [isIMAccount])
  return (
    <div>
      <Routes basename={RouteConstants.MYRAC}>
        <Route path={RouteConstants.ROOT} element={<MainContent />}>
          <Route index path={RouteConstants.LOGIN} element={<RegistrationLogin />} />
          <Route path={RouteConstants.ROOT} element={<LoggedInView fullWidth={isCarInsuranceRoute} />}>
            {coreRoutes.map((route) => <Route key={route.path || 'index-coreRoutes'} path={route.path} element={route.element} />)}
          </Route>
        </Route>
        <Route path={RouteConstants.RENEWAL} element={featureFlags?.isLDReady && featureFlags?.allowRenewalJourney === 1 && <RequireAuth><RenewalsView /></RequireAuth>}>
          {/* eslint-disable-next-line react/no-array-index-key */}
          {renewalRoutes.map((route, idx) => <Route key={`${route.path}-${idx}`} path={route.path} index={!!route.index} element={route.element} />)}
        </Route>
        <Route path={RouteConstants.RAC_LOYALTY_REWARDS} element={<LoyaltyRewardsView />}>
          {/* eslint-disable-next-line react/no-array-index-key */}
          {royaltyRewardsRoutes.map((route, idx) => <Route key={`${route.path}-${idx}`} path={route.path} index={!!route.index} element={route.element} />)}
        </Route>
      </Routes>
    </div>
  );
};

export default MyRac;
